import {
  Stack,
  Drawer,
  List,
  useMediaQuery,
  IconButton,
  Collapse,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Fragment, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useWelcomeQuery } from '../../api/getWelcomeDetails';
import useLogoutMutation from '../../api/logout';
import ChevronDownIcon from '../../assets/icons/ChevronDown';
import ChevronUpIcon from '../../assets/icons/ChevronUp';
import Logo from '../../assets/icons/Logo';
import colors from '../../constants/colors';
import useAnalytics from '../../hooks/useAnalytics';
import ListIcons, { ListIconsProps } from './components/ListIcons';
import MobileHeader from './components/Mobile';
import { DrawerItem, drawerMap } from './constants';
import useGetFeatureFlags from '../../hooks/useGetFeatureFlags';

const drawerWidth = 120;

const Header = () => {
  const [open, setOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<
    Map<string, boolean>
  >(new Map());
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('desktop'));
  const { track } = useAnalytics();
  const { data: welcomeData } = useWelcomeQuery();
  const logoutMutation = useLogoutMutation();
  const location = useLocation();
  const { enableSettings } = useGetFeatureFlags();

  const showPasswordSettings =
    enableSettings && welcomeData?.userInfo.loginType === 'cloud';

  const handleNavigation = (text: string) => {
    track(`${text} Accessed`, {
      event_type: 'Link Accessed',
      location: 'Left Navigational Pane',
      link: text,
    });
  };

  const renderDrawerItem = ({ path, ...item }: DrawerItem, index: number) => {
    const key = `${item.text}-${index}`;
    const propsToSend: ListIconsProps = { ...item };

    if (item.subCategory) {
      propsToSend.onClick = () =>
        setExpandedSections((prev) => {
          const newMap = new Map(prev);
          newMap.forEach((value, mapKey) => {
            newMap.set(mapKey, false);
          });
          newMap.set(item.text, !(prev.get(item.text) || false));
          return newMap;
        });
      propsToSend.isExpanded = expandedSections.get(item.text);
      propsToSend.arrow = expandedSections.get(item.text) ? (
        <ChevronUpIcon />
      ) : (
        <ChevronDownIcon />
      );
    }

    if (path) {
      propsToSend.path = path;
      propsToSend.previousPage = location.pathname;
      propsToSend.onClick = () => {
        window.location.pathname.match(/^\/edit\//) && setOpen(false);
        handleNavigation(item.text);
      };
    }

    return (
      <Fragment key={key}>
        <ListIcons {...propsToSend} showText={open} data-testid={key} />
        {!!item.subCategory && renderChildList({ path, ...item }, index)}
      </Fragment>
    );
  };

  const renderChildList = ({ path, ...item }: DrawerItem, index: number) => {
    if (!item.subCategory) {
      return null;
    }
    return (
      <Collapse
        key={`${item.text}-${index}-children`}
        component="li"
        in={(expandedSections.get(item.text) || false) && open}
        timeout="auto"
        unmountOnExit
      >
        <List sx={{ pl: 4 }}>
          {item.subCategory?.map((child, childIndex) =>
            renderDrawerItem(child, +`${index}${childIndex}`)
          )}
        </List>
      </Collapse>
    );
  };

  if (isMobile) {
    return <MobileHeader />;
  }

  return (
    <Drawer
      ref={drawerRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(e) => {
        if (!drawerRef?.current?.contains(e.relatedTarget)) setOpen(false);
      }}
      sx={{
        display: ['none', 'none', 'initial'],
        displayPrint: 'none',
        width: drawerWidth,
        flexShrink: 0,
        zIndex: 1300,
        '& .MuiDrawer-paper': {
          width: open ? 430 : drawerWidth,
          height: '100vh',
          boxSizing: 'border-box',
          border: 'none',
          boxShadow: '0px 8px 16px rgba(25, 30, 43, 0.16)',
        },
      }}
      variant="permanent"
      anchor="left"
    >
      <Stack direction="column" sx={{ height: 1 }}>
        <IconButton
          component={Link}
          aria-label="Home"
          disableRipple
          to="/"
          onClick={() => {
            track('Overview Accessed', {
              event_type: 'Link Accessed',
              location: 'Left Navigational Pane',
              link: 'Logo',
            });
          }}
          sx={{
            p: 0,
            justifyContent: 'inherit',
            my: 5,
            mx: 2,
            '&:focus': {
              outline: `solid 2px ${colors.csaaBlack}`,
              outlineOffset: '4px',
              borderRadius: 2,
              backgroundColor: 'inherit',
            },
            '&:hover': {
              background: 'transparent',
            },
          }}
        >
          <Logo
            sx={{
              height: { mobile: 24, desktop: 40 },
              width: { mobile: 47, desktop: 78 },
            }}
          />
        </IconButton>
        <Stack
          sx={{
            justifyContent: "space-between",
            p: 2,
            pt: 3,
            height: 1
          }}>
          <List>
            {[
              drawerMap.Home,
              drawerMap.Bill,
              drawerMap.Shield,
              drawerMap.Document,
              drawerMap.Clipboard,
              showPasswordSettings ? drawerMap.Settings : drawerMap.Envelope,
              drawerMap.TextBubble,
            ].map(renderDrawerItem)}
          </List>
          <List>
            <ListIcons
              path="#"
              {...drawerMap.SignOut}
              showText={open}
              onClick={async () => {
                try {
                  await logoutMutation.mutateAsync();
                } catch (err) {
                  // Error handled by mutation
                }
              }}
            />
          </List>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default Header;
